import React, { useState } from 'react';
import { financialTransactionAPI } from '../financialTransactionService';
import toast from 'react-hot-toast';
import { X, Loader, ShoppingBag } from 'lucide-react';
import { btn } from '../../utils/buttonStyles';

const CURRENT_YEAR = new Date().getFullYear();

const SEASONS = ['Year', 'H1', 'H2', 'Q1', 'Q2', 'Q3', 'Q4', 'Spring', 'Summer', 'Fall', 'Winter'];

/**
 * AddGroupExpenseModal
 *
 * Simple modal for admins to record spending from the group pool.
 * Creates a Debit FTS with no per-member credit transactions.
 *
 * Props:
 *   groupId  – group UUID
 *   onClose  – dismiss modal
 *   onSaved  – called after successful save
 *   theme
 */
const AddGroupExpenseModal = ({ groupId, expenseManagerId, onClose, onSaved, theme }) => {
  const [title, setTitle]   = useState('');
  const [amount, setAmount] = useState('');
  const [season, setSeason] = useState('Year');
  const [seasonYear, setSeasonYear] = useState(CURRENT_YEAR);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!title.trim() || isNaN(amt) || amt <= 0) {
      toast.error('Please enter a title and valid amount');
      return;
    }
    setSaving(true);
    try {
      await financialTransactionAPI.createGroupExpense({
        groupId,
        title: title.trim(),
        amount: amt,
        season,
        seasonYear,
        expenseManagerId,
      });
      toast.success('Expense recorded');
      onSaved?.();
    } catch (err) {
      toast.error(err.message || 'Failed to record expense');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className={`${theme.card} rounded-2xl border ${theme.border} w-full max-w-sm shadow-2xl`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${theme.border}`}>
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-4 h-4 text-orange-500" />
            <h2 className={`font-semibold ${theme.text}`}>Add Expense</h2>
          </div>
          <button onClick={onClose} className={`p-1.5 rounded-lg ${theme.textSecondary} hover:text-red-400 transition-colors`}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Title */}
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide ${theme.textSecondary} mb-1.5`}>
              What was it for?
            </label>
            <input
              type="text"
              placeholder="e.g. Balls, Cones, Jerseys"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className={`w-full px-3 py-2.5 rounded-xl border ${theme.border} ${theme.card} ${theme.text} text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/40`}
            />
          </div>

          {/* Amount */}
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide ${theme.textSecondary} mb-1.5`}>
              Amount ($)
            </label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              className={`w-full px-3 py-2.5 rounded-xl border ${theme.border} ${theme.card} ${theme.text} text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/40`}
            />
          </div>

          {/* Season + Year */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide ${theme.textSecondary} mb-1.5`}>
                Season
              </label>
              <select
                value={season}
                onChange={e => setSeason(e.target.value)}
                className={`w-full px-3 py-2.5 rounded-xl border ${theme.border} ${theme.card} ${theme.text} text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/40`}
              >
                {SEASONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide ${theme.textSecondary} mb-1.5`}>
                Year
              </label>
              <input
                type="number"
                value={seasonYear}
                onChange={e => {
                  const val = e.target.value;
                  setSeasonYear(val === '' ? '' : parseInt(val));
                }}
                onBlur={e => {
                  if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                    setSeasonYear(CURRENT_YEAR);
                  }
                }}
                className={`w-full px-3 py-2.5 rounded-xl border ${theme.border} ${theme.card} ${theme.text} text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/40`}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className={`${btn.base} ${btn.md} ${theme.buttonGhost} flex-1`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex-1 flex items-center justify-center gap-2`}
            >
              {saving && <Loader className="w-4 h-4 animate-spin" />}
              Record Expense
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddGroupExpenseModal;
